// <auto-generated>
// This file is automatically generated from DSL.
// Do not modify this file directly. Changes will be overwritten.
// Generated: 2026-06-16T14:14:19.244Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, SimulationCompleteError } from '@/lib/apiClient';

/** C7: a field required for the action's transition only while its AST condition holds. */
export interface ConditionalRequiredField {
  fieldPath: string;
  /** ConditionDto JSON over the entity's own fields (normalized by the server at authoring) */
  condition: string;
  message?: string;
}

export interface ActionMetadata {
  id: string;
  name: string;
  workflowName: string;
  stepName: string;
  permission: string;
  enabled: boolean;
  reason?: string;
  /** Server verdict on the SAVED entity; the menu re-derives it from current form data (C7). */
  missingFields?: string[];
  requiredFields?: string[];
  conditionalRequiredFields?: ConditionalRequiredField[];
}

export interface WorkflowActions {
  workflowName: string;
  actions: ActionMetadata[];
}

export interface CommandAction {
  id: string;
  name: string;
  displayName: string;
  permission: string;
  /** The state machine (bound entity field) the command drives; '' for a command declared with skipStateMachine. */
  stateMachine: string;
  enabled: boolean;
  reason?: string;
  /** Server verdict on the SAVED entity; the menu re-derives it from current form data (C7). */
  missingFields?: string[];
  requiredFields?: string[];
  conditionalRequiredFields?: ConditionalRequiredField[];
}

export interface AvailableActionsResponse {
  workflows: WorkflowActions[];
  commands?: CommandAction[];
}

export interface TriggerWorkflowInput {
  workflowName: string;
  stepName: string;
}

export interface ExecuteCommandInput {
  commandName: string;
  input?: Record<string, any>;
}

/**
 * Hook for managing PlanHeader actions
 */
export function usePlanHeaderActions(entityId: string) {
  const queryClient = useQueryClient();
  // Fetch available actions
  const { data: availableActions, isLoading, error } = useQuery<AvailableActionsResponse>({
    queryKey: ['planheader', entityId, 'available-actions'],
    queryFn: async () => {
      const response = await apiClient.get<AvailableActionsResponse>(
        `/api/finance/planheader/${entityId}/available-actions`
      );
      return response.data;
    },
    enabled: !!entityId && entityId !== 'new',
  });

  // Trigger workflow mutation
  const triggerWorkflow = useMutation({
    mutationFn: async (input: TriggerWorkflowInput) => {
      const response = await apiClient.post(
        `/api/finance/planheader/${entityId}/trigger-workflow`,
        input
      );
      return response.data;
    },
    onSuccess: () => {
      // Refresh entity data (plural key root — matches planheaderKeys.all) and available actions
      queryClient.invalidateQueries({ queryKey: ['planheaders'] });
      queryClient.invalidateQueries({ queryKey: ['planheader', entityId, 'available-actions'] });
    },
    onError: (error: unknown) => {
      if (error instanceof SimulationCompleteError) return;
      // Error is propagated to the caller via mutation state for inline display
    },
  });

  // Execute command mutation
  const executeCommand = useMutation({
    mutationFn: async (input: ExecuteCommandInput) => {
      const response = await apiClient.post(
        `/api/finance/planheader/${entityId}/${input.commandName.toLowerCase()}`,
        input.input || {}
      );
      return response.data;
    },
    onSuccess: () => {
      // Refresh entity data (plural key root — matches planheaderKeys.all) and available actions
      queryClient.invalidateQueries({ queryKey: ['planheaders'] });
      queryClient.invalidateQueries({ queryKey: ['planheader', entityId, 'available-actions'] });
    },
    onError: (_error: unknown) => {
      // Error is propagated to the caller via mutation state for inline display
    },
  });

  // Debug: Log the response to help troubleshoot
  if (availableActions && process.env.NODE_ENV === 'development') {
    console.log('Available actions response:', availableActions);
    console.log('Workflows:', availableActions.workflows);
    console.log('Commands:', availableActions.commands);
  }

  // Map commands from backend format (name) to frontend format (displayName)
  // Backend sends ActionMetadataDto with Name property, but frontend expects displayName
  const mappedCommands = (availableActions?.commands || []).map(cmd => ({
    ...cmd,
    displayName: cmd.name || cmd.id, // Use name as displayName, fallback to id
    name: cmd.id, // Use id as name for command execution
  }));

  return {
    availableActions: availableActions?.workflows || [],
    availableCommands: mappedCommands,
    isLoading,
    error,
    triggerWorkflow: triggerWorkflow.mutate,
    executeCommand: executeCommand.mutate,
    executeCommandMutation: executeCommand, // Expose full mutation object for error handling
    isTriggering: triggerWorkflow.isPending,
    isExecuting: executeCommand.isPending,
    refetchActions: () => {
      queryClient.invalidateQueries({ queryKey: ['planheader', entityId, 'available-actions'] });
    },
  };
}
